import { Box, CircularProgress, Grid2 as Grid, Link, Paper, Typography } from '@mui/material'
import useAuth from 'hooks/useAuth'
import { useFetchOrganization } from 'hooks/useOrganizations'
import React, { useEffect, useState, useTransition } from 'react'
import type { UserRole } from 'types/role'
import { hasFeature } from 'types/user-profile'
import OrganizationAPIMgmtSection from './OrganizationAPIMgmtSection/index'
import OrganizationConnectionMgmtSection from './OrganizationConnectionMgmtSection/index'

const SecuritySettingsPage: React.FC = () => {
  const { token, profile, isLoading: isLoadingProfile } = useAuth()

  const [organizationId, setOrganizationId] = useState('')
  const [isPending, startTransition] = useTransition()
  const { isLoading: isLoadingOrg, isError: isErrorOrg, organization } = useFetchOrganization(organizationId, token!)

  // New function to find the first top-level organization
  const findFirstTopLevelOrg = (roles: UserRole[]) => {
    if (!roles) return ''

    // Create a map of all organizations
    const organizationMap = new Map(roles.map((role) => [role.organization.id, role.organization]))

    // Find the first org that doesn't have a parent or its parent isn't in our org list
    const firstTopLevelOrg = roles.find(
      (role) => !role.organization.parentId || !organizationMap.has(role.organization.parentId)
    )

    return firstTopLevelOrg?.organization.id || ''
  }

  useEffect(() => {
    if (organizationId === '' && profile && profile?.roles?.length > 0) {
      const firstTopLevelOrgId = findFirstTopLevelOrg(profile.roles)
      startTransition(() => {
        setOrganizationId(firstTopLevelOrgId)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile])

  const isLoading = isLoadingOrg || isLoadingProfile || isPending
  const isError = isErrorOrg

  if (isLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ height: '100vh' }}>
        <CircularProgress color="primary" />
      </Grid>
    )
  }

  if (isError) {
    return <div>Failed to load organization data</div>
  }

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'baseline' },
          marginBottom: '20px',
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography variant="h2">Integration Settings</Typography>
      </Box>
      {isPending ? (
        <Grid container justifyContent="center" alignItems="center" style={{ height: '100vh' }}>
          <CircularProgress color="primary" />
        </Grid>
      ) : (
        <>
          {organization && hasFeature(profile!, 'api') && <OrganizationAPIMgmtSection organization={organization} />}
          {organization && hasFeature(profile!, 'finch') && (
            <OrganizationConnectionMgmtSection organization={organization} />
          )}
          {!hasFeature(profile!, 'api') && (
            <Paper sx={{ mb: 3, p: 3, height: '100%' }}>
              <Typography variant="subtitle1" sx={{ color: 'white', opacity: 0.9 }} gutterBottom>
                Coming Soon: Purcent API,{' '}
                <Link href="mailto:support@purcent.com" sx={{ color: 'inherit', textDecoration: 'underline' }}>
                  Contact support
                </Link>{' '}
                for more information.
              </Typography>
            </Paper>
          )}
          {!hasFeature(profile!, 'finch') && (
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="subtitle1" sx={{ color: 'white', opacity: 0.9 }} gutterBottom>
                Coming Soon: HRIS Connectivity,{' '}
                <Link href="mailto:support@purcent.com" sx={{ color: 'inherit', textDecoration: 'underline' }}>
                  Contact support
                </Link>{' '}
                for early access consideration.
              </Typography>
            </Paper>
          )}
        </>
      )}
    </>
  )
}

export default SecuritySettingsPage
